import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data } = await api.get('/admin/settings');
      setSettings(data);
    } catch (err) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put('/admin/settings', settings);
      toast.success('Settings saved!');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) return <div className="flex items-center justify-center h-64"><p className="text-gray-400">Loading settings...</p></div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">⚙️ Settings</h1>
        <button onClick={saveSettings} disabled={saving}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-primary-700 disabled:opacity-50">
          {saving ? 'Saving...' : '💾 Save Settings'}
        </button>
      </div>

      <div className="space-y-6">
        {/* Bank Details */}
        <section className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">🏦 Bank Details (Garhwal Hikes)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Account Name" value={settings.bankDetails.accountName}
              onChange={v => setSettings(s => ({ ...s, bankDetails: { ...s.bankDetails, accountName: v } }))} />
            <Field label="Account Number" value={settings.bankDetails.accountNumber}
              onChange={v => setSettings(s => ({ ...s, bankDetails: { ...s.bankDetails, accountNumber: v } }))} />
            <Field label="IFSC Code" value={settings.bankDetails.ifsc}
              onChange={v => setSettings(s => ({ ...s, bankDetails: { ...s.bankDetails, ifsc: v } }))} />
            <Field label="Bank Name" value={settings.bankDetails.bankName}
              onChange={v => setSettings(s => ({ ...s, bankDetails: { ...s.bankDetails, bankName: v } }))} />
            <Field label="Branch" value={settings.bankDetails.branch}
              onChange={v => setSettings(s => ({ ...s, bankDetails: { ...s.bankDetails, branch: v } }))} />
            <Field label="UPI ID" value={settings.bankDetails.upiId}
              onChange={v => setSettings(s => ({ ...s, bankDetails: { ...s.bankDetails, upiId: v } }))} />
          </div>
        </section>

        {/* Company Contact */}
        <section className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">📞 Company Contact (Garhwal Hikes)</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Phone" value={settings.companyContact.phone}
              onChange={v => setSettings(s => ({ ...s, companyContact: { ...s.companyContact, phone: v } }))} />
            <Field label="Email" value={settings.companyContact.email}
              onChange={v => setSettings(s => ({ ...s, companyContact: { ...s.companyContact, email: v } }))} />
            <Field label="WhatsApp" value={settings.companyContact.whatsapp}
              onChange={v => setSettings(s => ({ ...s, companyContact: { ...s.companyContact, whatsapp: v } }))} />
            <Field label="Website" value={settings.companyContact.website}
              onChange={v => setSettings(s => ({ ...s, companyContact: { ...s.companyContact, website: v } }))} />
            <Field label="Address" value={settings.companyContact.address}
              onChange={v => setSettings(s => ({ ...s, companyContact: { ...s.companyContact, address: v } }))} />
          </div>
        </section>

        {/* YatraYatri Brand */}
        <section className="bg-white rounded-xl shadow-sm border p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">🧭 YatraYatri Brand</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Phone" value={settings.yatrayatri?.companyContact?.phone}
              onChange={v => setSettings(s => ({ ...s, yatrayatri: { ...s.yatrayatri, companyContact: { ...s.yatrayatri?.companyContact, phone: v } } }))} />
            <Field label="Email" value={settings.yatrayatri?.companyContact?.email}
              onChange={v => setSettings(s => ({ ...s, yatrayatri: { ...s.yatrayatri, companyContact: { ...s.yatrayatri?.companyContact, email: v } } }))} />
            <Field label="WhatsApp" value={settings.yatrayatri?.companyContact?.whatsapp}
              onChange={v => setSettings(s => ({ ...s, yatrayatri: { ...s.yatrayatri, companyContact: { ...s.yatrayatri?.companyContact, whatsapp: v } } }))} />
            <Field label="Website" value={settings.yatrayatri?.companyContact?.website}
              onChange={v => setSettings(s => ({ ...s, yatrayatri: { ...s.yatrayatri, companyContact: { ...s.yatrayatri?.companyContact, website: v } } }))} />
            <Field label="Address" value={settings.yatrayatri?.companyContact?.address}
              onChange={v => setSettings(s => ({ ...s, yatrayatri: { ...s.yatrayatri, companyContact: { ...s.yatrayatri?.companyContact, address: v } } }))} />
            <Field label="GST Number" value={settings.yatrayatri?.gstNumber}
              onChange={v => setSettings(s => ({ ...s, yatrayatri: { ...s.yatrayatri, gstNumber: v } }))} />
          </div>
          <h3 className="text-sm font-semibold text-gray-700 mt-6 mb-3">🏦 Bank Details (YatraYatri)</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Account Name" value={settings.yatrayatri?.bankDetails?.accountName}
              onChange={v => setSettings(s => ({ ...s, yatrayatri: { ...s.yatrayatri, bankDetails: { ...s.yatrayatri?.bankDetails, accountName: v } } }))} />
            <Field label="Account Number" value={settings.yatrayatri?.bankDetails?.accountNumber}
              onChange={v => setSettings(s => ({ ...s, yatrayatri: { ...s.yatrayatri, bankDetails: { ...s.yatrayatri?.bankDetails, accountNumber: v } } }))} />
            <Field label="IFSC Code" value={settings.yatrayatri?.bankDetails?.ifsc}
              onChange={v => setSettings(s => ({ ...s, yatrayatri: { ...s.yatrayatri, bankDetails: { ...s.yatrayatri?.bankDetails, ifsc: v } } }))} />
            <Field label="Bank Name" value={settings.yatrayatri?.bankDetails?.bankName}
              onChange={v => setSettings(s => ({ ...s, yatrayatri: { ...s.yatrayatri, bankDetails: { ...s.yatrayatri?.bankDetails, bankName: v } } }))} />
            <Field label="Branch" value={settings.yatrayatri?.bankDetails?.branch}
              onChange={v => setSettings(s => ({ ...s, yatrayatri: { ...s.yatrayatri, bankDetails: { ...s.yatrayatri?.bankDetails, branch: v } } }))} />
            <Field label="UPI ID" value={settings.yatrayatri?.bankDetails?.upiId}
              onChange={v => setSettings(s => ({ ...s, yatrayatri: { ...s.yatrayatri, bankDetails: { ...s.yatrayatri?.bankDetails, upiId: v } } }))} />
            <Field label="UPI Number" value={settings.yatrayatri?.bankDetails?.upiNumber}
              onChange={v => setSettings(s => ({ ...s, yatrayatri: { ...s.yatrayatri, bankDetails: { ...s.yatrayatri?.bankDetails, upiNumber: v } } }))} />
          </div>
        </section>
      </div>
    </div>
  );
}

function Field({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      <input type="text" value={value || ''} onChange={e => onChange(e.target.value)}
        className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none" />
    </div>
  );
}
